import type { HttpEvent, HttpHandlerFn, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import type { Observable } from 'rxjs';
import { inject, Injector } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { filter, switchMap, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { ErrorEventType, EventName, EventService, guid, LoggerService } from '@accesso/cc-std-lib';
import { LocaleModel } from '@accesso/cc-ui/model';
import { JsonApiService } from '@accesso/ng-json-api';
import { BannerService, createErrorBannerMessage } from '@accesso/ng-uds/banners';

import { ErrorSection } from '../localization/locales';

const LOG_PREFIX = '[anon-token.interceptor.ts]';

export const ANON_TOKEN_BANNER_ID = guid();

export const anonTokenInterceptor: HttpInterceptorFn = (request: HttpRequest<unknown>, next: HttpHandlerFn): Observable<HttpEvent<unknown>> => {
	if (!request.url.includes('/internal/anonymousToken')) return next(request);

	const banner = inject(BannerService);
	const logger = inject(LoggerService);
	const event = inject(EventService);
	const jsonApi = inject(JsonApiService);
	const injector = inject(Injector);

	return next(request).pipe(
		catchError((response) => {
			logger.error(`${LOG_PREFIX} Error fetching anonymous token:`, response);

			const locale = jsonApi.findOne(LocaleModel, ErrorSection);

			return toObservable(locale.state, { injector }).pipe(
				filter(({ loading }) => !loading),
				switchMap(() => {
					const message = locale.attributes()?.['processingrequest'] ?? 'An unexpected error has occurred';

					banner.add({ ...createErrorBannerMessage(message), id: ANON_TOKEN_BANNER_ID });

					event.publish({
						event: EventName.ERROR,
						details: {
							error_type: ErrorEventType.ANONYMOUS_TOKEN,
							message,
						},
					});

					return throwError(() => response);
				})
			);
		})
	);
};
